import "@/App.css";
import "@/index.css";
import React, { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FavouritesProvider } from "@/context/FavouritesContext";
import OnThisDay from "@/pages/OnThisDay";
import Browse from "@/pages/Browse";
import FactDetail from "@/pages/FactDetail";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import MagicLogin from "@/pages/MagicLogin";
import MagicVerify from "@/pages/MagicVerify";
import Saved from "@/pages/Saved";
import Birthdays from "@/pages/Birthdays";
import Account from "@/pages/Account";
import About from "@/pages/About";
import Horoscope from "@/pages/Horoscope";
import Contact from "@/pages/Contact";
import Privacy from "@/pages/Privacy";
import Unsubscribe from "@/pages/Unsubscribe";
import AdminSubscribers from "@/pages/AdminSubscribers";
import AdminEvents from "@/pages/AdminEvents";
import AdminObservances from "@/pages/AdminObservances";
import AdminHallOfFame from "@/pages/AdminHallOfFame";
import AdminNumberOnes from "@/pages/AdminNumberOnes";
import NumberOnes from "@/pages/NumberOnes";
import ChartQuiz from "@/pages/ChartQuiz";
import Died from "@/pages/Died";
import EditorialPolicy from "@/pages/EditorialPolicy";
import Sports from "@/pages/Sports";
import HistoryDay from "@/pages/HistoryDay";
import OddlyTrueNews from "@/pages/OddlyTrueNews";
import Battles from "@/pages/Battles";
import Movies from "@/pages/Movies";
import AdminTools from "@/pages/AdminTools";

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to={adminOnly ? "/admin/login" : "/login"} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/admin/login" replace />;
  return children;
};

function AuthCallback() {
  const processed = useRef(false);
  const { processGoogleSession } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    const sid = m ? decodeURIComponent(m[1]) : null;
    (async () => {
      if (sid) {
        try { await processGoogleSession(sid); } catch (e) { /* ignore */ }
      }
      window.history.replaceState(null, "", window.location.pathname);
      navigate("/", { replace: true });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-10 h-10 rounded-full border-2 border-gold border-t-transparent animate-spin" />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // Detect Google OAuth return synchronously (must run before ProtectedRoute)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<OnThisDay />} />
      <Route path="/vault" element={<Navigate to="/" replace />} />
      <Route path="/browse" element={<Browse />} />
      <Route path="/number-ones" element={<NumberOnes />} />
      <Route path="/quiz" element={<ChartQuiz />} />
      <Route path="/died" element={<Died />} />
      <Route path="/infamous" element={<Navigate to="/died" replace />} />
      <Route path="/birthdays" element={<Birthdays />} />
      <Route path="/sports" element={<Sports />} />
      <Route path="/history/:slug" element={<HistoryDay />} />
      <Route path="/history/:month/:day" element={<HistoryDay />} />
      <Route path="/on-this-day" element={<HistoryDay />} />
      <Route path="/on-this-day/:slug" element={<HistoryDay />} />
      <Route path="/battles" element={<Battles />} />
      <Route path="/battles/:era" element={<Battles />} />
      <Route path="/movies" element={<Movies />} />
      <Route path="/movies/:decade" element={<Movies />} />
      <Route path="/oddlytrue-news" element={<OddlyTrueNews />} />
      <Route path="/oddlytrue-news/:slug" element={<OddlyTrueNews />} />
      <Route path="/on-this-day" element={<Navigate to="/" replace />} />
      <Route path="/fact/:slug" element={<FactDetail />} />
      <Route path="/horoscope" element={<Horoscope />} />
      <Route path="/about" element={<About />} />
      <Route path="/editorial-policy" element={<EditorialPolicy />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/login" element={<MagicLogin />} />
      <Route path="/register" element={<Navigate to="/login" replace />} />
      <Route path="/auth/magic" element={<MagicVerify />} />
      <Route path="/saved" element={<ProtectedRoute><Saved /></ProtectedRoute>} />
      <Route path="/favourites" element={<Navigate to="/saved" replace />} />
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/subscribers" element={<ProtectedRoute adminOnly><AdminSubscribers /></ProtectedRoute>} />
      <Route path="/admin/events" element={<ProtectedRoute adminOnly><AdminEvents /></ProtectedRoute>} />
      <Route path="/admin/observances" element={<ProtectedRoute adminOnly><AdminObservances /></ProtectedRoute>} />
      <Route path="/admin/halloffame" element={<ProtectedRoute adminOnly><AdminHallOfFame /></ProtectedRoute>} />
      <Route path="/admin/number-ones" element={<ProtectedRoute adminOnly><AdminNumberOnes /></ProtectedRoute>} />
      <Route path="/admin/tools" element={<ProtectedRoute adminOnly><AdminTools /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <FavouritesProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-center" theme="light" />
        </FavouritesProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
